import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShoppingCart, faUser, faPhone, faCalendarAlt, faFileInvoice, faBox,
  faRupeeSign, faExclamationTriangle, faBarcode, faPlus, faTrash, faSave,
  faTimes, faSpinner, faCheckCircle, faExclamationCircle, faSearch,
  faUpload, faReceipt, faChevronDown, faBoxes, faShip, faRoad, faComment,
  faBuilding, faTruck, faRedo, faCircleCheck, faCircleInfo, faCircleExclamation,
  faFileImage, faUniversity, faCreditCard, faMoneyBillWave, faMoneyCheck,
  faQrcode, faCamera, faClock, faMoneyBill, faDownload, faExchangeAlt,
  faRulerCombined, faListOl, faCubes, faInfoCircle as faInfoCircleIcon
} from '@fortawesome/free-solid-svg-icons';

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => { clearTimeout(timeout); func(...args); };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Translator
const createPlaceOrderTranslator = () => {
  let cache = new Map();
  let translationListeners = new Set();
  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem('ourzest_placeorder_translations');
      if (cached) {
        const parsed = JSON.parse(cached);
        Object.entries(parsed).forEach(([key, value]) => cache.set(key, value));
      }
    } catch (e) { console.log("No cache found"); }
  };
  const saveToCache = () => {
    try {
      const cacheObj = {};
      cache.forEach((value, key) => { cacheObj[key] = value; });
      localStorage.setItem('ourzest_placeorder_translations', JSON.stringify(cacheObj));
    } catch (e) { console.error("Error saving cache:", e); }
  };
  const translateAPI = async (text, targetLang) => {
    if (targetLang === 'en') return text;
    try {
      const response = await fetch('https://translate.argosopentech.com/translate', {
        method: 'POST',
        body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) { const data = await response.json(); return data.translatedText; }
    } catch (e) { console.log("LibreTranslate failed"); }
    try {
      const response = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`);
      if (response.ok) { const data = await response.json(); return data.responseData.translatedText; }
    } catch (e) { console.log("MyMemory failed"); }
    return text;
  };
  const translateText = async (text, targetLang) => {
    const cacheKey = `${text}_${targetLang}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);
    const translated = await translateAPI(text, targetLang);
    cache.set(cacheKey, translated);
    saveToCache();
    return translated;
  };
  const defaultTexts = {
    addOrder: 'Place Order', editOrder: 'Edit Order', orderDetails: 'Order Details',
    orderNumber: 'Order Number', orderDate: 'Order Date', expectedDelivery: 'Expected Delivery',
    supplier: 'Supplier', selectSupplier: 'Select supplier', searchSupplier: 'Search supplier...',
    supplierName: 'Supplier Name', company: 'Company', companyOptional: 'Company (Optional)',
    product: 'Product', searchProduct: 'Search product...', productName: 'Product Name',
    colorCode: 'Color Code', category: 'Category', selectCategory: 'Select category',
    totalYards: 'Total Yards (Stock)', totalRolls: 'Total Rolls',
    price: 'Price', quantity: 'Quantity', totalAmount: 'Total Amount',
    status: 'Status', orderPlaced: 'Order Placed', processing: 'Processing', shipped: 'Shipped',
    delivered: 'Delivered', cancelled: 'Cancelled', containerNo: 'Container/Conveyance No',
    containerOptional: 'Container No', routeDetails: 'Route Details', routeOptional: 'Route details',
    paidAmount: 'Paid Amount', balance: 'Balance', paymentMethod: 'Payment Method',
    cash: 'Cash', bankTransfer: 'Bank Transfer', cheque: 'Cheque', creditCard: 'Credit Card', other: 'Other', transfer: 'Transfer (Transaction OUT)',
    remarks: 'Remarks', remarksOptional: 'Remarks', saveOrder: 'Save Order', updateOrder: 'Update Order',
    clear: 'Clear', saving: 'Saving...', orderSaved: 'Order saved successfully!',
    orderUpdated: 'Order updated successfully!', failedSave: 'Failed to save order',
    failedUpdate: 'Failed to update order', networkError: 'Network error occurred',
    supplierRequired: 'Supplier is required', productRequired: 'Product is required',
    priceOptional: 'Price', quantityRequired: 'Quantity is required',
    statusRequired: 'Status is required', invalidPrice: 'Please enter a valid price',
    invalidQuantity: 'Please enter a valid quantity', invalidPaid: 'Please enter a valid paid amount',
    success: 'Success', error: 'Error', warning: 'Warning', optional: 'Optional',
    selectUnit: 'Unit', generate: 'Generate', browse: 'Browse',
    noProductsFound: 'No products found', noSuppliersFound: 'No suppliers found',
    addItem: 'Add Item', removeItem: 'Remove Item', totalItems: 'Total Items',
    yards: 'Yards', meters: 'Meters',
    orderReceipt: 'Order Receipt', uploadReceipt: 'Upload Receipt', receiptUploaded: 'Receipt uploaded',
    currency: 'Currency', currencyRate: 'Exchange Rate', todayRate: "Today's Rate",
    selectPaymentMethod: 'Select Payment Method', manualTotals: 'Manual Totals',
    totalYardsInfo: 'Total yards is the actual stock quantity',
    totalYardsRequired: 'Total yards is required',
    orderQuantity: 'Order Quantity',
    orderRolls: 'Order Rolls'
  };
  const getTranslations = async (lang) => {
    if (lang === 'en') return defaultTexts;
    const translations = {};
    const promises = [];
    Object.keys(defaultTexts).forEach(key => {
      const cacheKey = `${defaultTexts[key]}_${lang}`;
      if (cache.has(cacheKey)) { translations[key] = cache.get(cacheKey); }
      else { promises.push(translateText(defaultTexts[key], lang).then(translated => { translations[key] = translated; })); }
    });
    if (promises.length > 0) { await Promise.all(promises); saveToCache(); }
    return translations;
  };
  const addListener = (callback) => { translationListeners.add(callback); };
  const removeListener = (callback) => { translationListeners.delete(callback); };
  const notifyListeners = (lang) => { translationListeners.forEach(callback => callback(lang)); };
  const checkLanguageChange = () => {
    const currentLang = localStorage.getItem('ourzest_language') || 'en';
    notifyListeners(currentLang);
  };
  const startLanguageCheck = () => {
    checkLanguageChange();
    const intervalId = setInterval(checkLanguageChange, 500);
    window.addEventListener('storage', checkLanguageChange);
    return () => { clearInterval(intervalId); window.removeEventListener('storage', checkLanguageChange); };
  };
  loadFromCache();
  return { getTranslations, translateText, addListener, removeListener, startLanguageCheck };
};
const placeOrderTranslator = createPlaceOrderTranslator();

// Currency Selector Component
const CurrencySelector = ({ currentLanguage, onCurrencyChange, onRateChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [exchangeRates, setExchangeRates] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    const saved = localStorage.getItem('ourzest_currency');
    return saved ? JSON.parse(saved) : { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee', flag: '🇵🇰', shortcut: 'P' };
  });
  const [currentRate, setCurrentRate] = useState(1.0000);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

  const currencies = [
    { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee', flag: '🇵🇰', shortcut: 'P', fullName: 'Pakistani Rupee' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', flag: '🇦🇪', shortcut: 'D', fullName: 'UAE Dirham' },
    { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸', shortcut: 'U', fullName: 'US Dollar' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳', shortcut: 'Y', fullName: 'Chinese Yuan' },
    { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺', shortcut: 'E', fullName: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧', shortcut: 'G', fullName: 'British Pound' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳', shortcut: 'I', fullName: 'Indian Rupee' },
    { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', flag: '🇸🇦', shortcut: 'S', fullName: 'Saudi Riyal' }
  ];

  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/PKR');
        const data = await response.json();
        if (data.rates) {
          setExchangeRates(data.rates);
          if (selectedCurrency.code !== 'PKR') {
            const rate = data.rates[selectedCurrency.code] || 1.0000;
            setCurrentRate(rate);
            if (onRateChange) onRateChange(rate);
          }
        }
      } catch (error) {
        console.error('Error fetching exchange rates:', error);
        const fallbackRates = { 'USD': 0.0036, 'AED': 0.013, 'CNY': 0.026, 'EUR': 0.0033, 'GBP': 0.0028, 'INR': 0.30, 'SAR': 0.013 };
        setExchangeRates(fallbackRates);
        if (selectedCurrency.code !== 'PKR') {
          const rate = fallbackRates[selectedCurrency.code] || 1.0000;
          setCurrentRate(rate);
          if (onRateChange) onRateChange(rate);
        }
      }
    };
    fetchExchangeRates();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCurrencies = currencies.filter(currency =>
    currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    currency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    currency.fullName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) setTimeout(() => { searchInputRef.current?.focus(); }, 100);
  };

  const handleCurrencySelect = (currency) => {
    const rate = currency.code === 'PKR' ? 1.0000 : (exchangeRates[currency.code] || 1.0000);
    setSelectedCurrency(currency);
    setCurrentRate(rate);
    localStorage.setItem('ourzest_currency', JSON.stringify(currency));
    setIsOpen(false);
    setSearchTerm('');
    if (onCurrencyChange) onCurrencyChange(currency);
    if (onRateChange) onRateChange(rate);
  };

  const handleRateChange = (e) => {
    const newRate = parseFloat(e.target.value) || 1.0000;
    setCurrentRate(newRate);
    if (onRateChange) onRateChange(newRate);
  };

  return (
    <div className="currency-selector" ref={dropdownRef}>
      <div className="currency-selector-row">
        <button className={`currency-button ${isOpen ? 'active' : ''}`} onClick={toggleDropdown}>
          <span className="currency-flag">{selectedCurrency.flag}</span>
          <span className="currency-code">{selectedCurrency.code}</span>
          <span className="currency-symbol">{selectedCurrency.symbol}</span>
          <FontAwesomeIcon icon={faChevronDown} />
        </button>
        <div className="currency-rate-field">
          <input
            type="number"
            id="currency_rate"
            name="currency_rate"
            value={currentRate}
            onChange={handleRateChange}
            step={selectedCurrency.code === 'PKR' ? "0.01" : "0.0001"}
            min="0.0001"
            className="rate-input-field"
            disabled={selectedCurrency.code === 'PKR'}
          />
        </div>
      </div>
      {isOpen && (
        <div className="currency-dropdown">
          <div className="currency-search">
            <FontAwesomeIcon icon={faSearch} className="currency-search-icon" />
            <input ref={searchInputRef} type="text" placeholder="Search currency..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} dir={isRTL ? 'rtl' : 'ltr'} />
          </div>
          <div className="currency-list">
            {filteredCurrencies.length > 0 ? (
              filteredCurrencies.map((currency) => (
                <div key={currency.code} className={`currency-item ${selectedCurrency.code === currency.code ? 'selected' : ''}`}
                  onClick={() => handleCurrencySelect(currency)}>
                  <div className="currency-info">
                    <span className="currency-flag">{currency.flag}</span>
                    <div className="currency-details">
                      <span className="currency-name">{currency.code}</span>
                      <span className="currency-fullname">{currency.fullName}</span>
                    </div>
                  </div>
                  <div className="currency-symbol">{currency.symbol}</div>
                </div>
              ))
            ) : (
              <div className="currency-no-results"><FontAwesomeIcon icon={faExclamationTriangle} /><span>No currencies found</span></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
  const getIconForType = (type) => {
    switch (type) {
      case 'success': return faCheckCircle;
      case 'error': return faExclamationCircle;
      case 'warning': return faExclamationTriangle;
      default: return faCheckCircle;
    }
  };
  const getTitleForType = (type) => {
    switch (type) {
      case 'success': return translations?.success || 'Success';
      case 'error': return translations?.error || 'Error';
      case 'warning': return translations?.warning || 'Warning';
      default: return '';
    }
  };
  if (!toast.show) return null;
  const opacity = Math.max(toast.progress / 100, 0.1);
  return (
    <div className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`} style={{ opacity }}>
      <div className="toast-content">
        <div className="toast-icon"><FontAwesomeIcon icon={getIconForType(toast.type)} /></div>
        <div className="toast-text">
          <div className="toast-title">{getTitleForType(toast.type)}</div>
          <div className="toast-message">{toast.message}</div>
        </div>
        <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      <div className="toast-timer">
        <div className="toast-progress-bar" style={{ width: `${toast.progress}%`, transition: 'width 0.1s linear' }}></div>
        <div className="toast-countdown">{toast.timeLeft}s</div>
      </div>
    </div>
  );
};

// Items Table Component - Removed Category column, added Color Code column
const PlaceOrderItemsTable = ({
  items, onItemChange, onRemove, onAdd, errors, disabled,
  translations, isRTL, user, shopId, showToast, currencySymbol
}) => {
  const tableRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productSearchTerms, setProductSearchTerms] = useState({});
  const [showProductDropdown, setShowProductDropdown] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const productSearchRefs = useRef({});

  // Unit options
  const unitOptions = [
    { value: 'yards', label: translations?.yards || 'Yards' },
    { value: 'meters', label: translations?.meters || 'Meters' }
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load products
  const loadProducts = async () => {
    if (!user?.username || !shopId) return;
    setLoadingProducts(true);
    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/fab_products.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'get_records',
          username: user.username, 
          shop_id: shopId, 
          page: 1, 
          limit: 100 
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setProducts(data.data || []);
        setFilteredProducts(data.data || []);
      }
    } catch (error) { 
      console.error('Error loading products:', error); 
    }
    finally { setLoadingProducts(false); }
  };

  useEffect(() => { 
    if (user?.username && shopId) loadProducts(); 
  }, [user?.username, shopId]);

  // Search products
  const handleProductSearch = async (index, term) => {
    setProductSearchTerms(prev => ({ ...prev, [index]: term }));
    if (!user?.username || !shopId) return;
    
    if (term.trim() === '') {
      setFilteredProducts(products);
      setShowProductDropdown(prev => ({ ...prev, [index]: true }));
      return;
    }
    
    const filtered = products.filter(product =>
      (product.product_name && product.product_name.toLowerCase().includes(term.toLowerCase())) ||
      (product.product_colorcode && product.product_colorcode.toLowerCase().includes(term.toLowerCase()))
    );
    
    if (filtered.length > 0) {
      setFilteredProducts(filtered);
      setShowProductDropdown(prev => ({ ...prev, [index]: true }));
    } else {
      setLoadingProducts(true);
      try {
        const response = await fetch('https://ourzestapp.online/oz_backend/fab_products.php', {
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            action: 'get_records',
            username: user.username, 
            shop_id: shopId, 
            filters: { product_name: term },
            page: 1, 
            limit: 20 
          })
        });
        const data = await response.json();
        if (data.status === 'success') {
          setFilteredProducts(data.data || []);
          setShowProductDropdown(prev => ({ ...prev, [index]: data.data && data.data.length > 0 }));
        } else { 
          setFilteredProducts([]); 
        }
      } catch (error) { 
        console.error('Error searching products:', error); 
        setFilteredProducts([]); 
      }
      finally { setLoadingProducts(false); }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(productSearchRefs.current).forEach(index => {
        if (productSearchRefs.current[index] && !productSearchRefs.current[index].contains(event.target)) {
          setShowProductDropdown(prev => ({ ...prev, [index]: false }));
        }
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectProduct = async (index, product) => {
    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/fab_products.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'get_record', 
          id: product.id, 
          username: user?.username, 
          shop_id: shopId 
        })
      });
      const data = await response.json();

      let productData;
      if (data.status === 'success' && data.data) {
        const fullProduct = data.data;
        productData = {
          product_id: fullProduct.id,
          product_name: fullProduct.product_name || '',
          product_colorcode: fullProduct.product_colorcode || '',
          manual_total_yards: '',
          manual_total_rolls: '',
          price: ''
        };
      } else {
        productData = {
          product_id: product.id,
          product_name: product.product_name || '',
          product_colorcode: product.product_colorcode || '',
          manual_total_yards: '',
          manual_total_rolls: '',
          price: ''
        };
      }

      onItemChange(index, 'product_id', productData.product_id);
      onItemChange(index, 'product_name', productData.product_name);
      onItemChange(index, 'product_colorcode', productData.product_colorcode);
      onItemChange(index, 'manual_total_yards', productData.manual_total_yards);
      onItemChange(index, 'manual_total_rolls', productData.manual_total_rolls);
      onItemChange(index, 'price', productData.price);
      onItemChange(index, 'is_manual_totals', true);

      const displayName = productData.product_name + (productData.product_colorcode ? ` - ${productData.product_colorcode}` : '');
      setProductSearchTerms(prev => ({ ...prev, [index]: displayName }));
      setShowProductDropdown(prev => ({ ...prev, [index]: false }));
      if (showToast) showToast('success', `Product "${productData.product_name}" selected`);
    } catch (error) {
      console.error('Error fetching product details:', error);
      const productData = {
        product_id: product.id, 
        product_name: product.product_name || '',
        product_colorcode: product.product_colorcode || '', 
        manual_total_yards: '',
        manual_total_rolls: '',
        price: ''
      };
      onItemChange(index, 'product_id', productData.product_id);
      onItemChange(index, 'product_name', productData.product_name);
      onItemChange(index, 'product_colorcode', productData.product_colorcode);
      onItemChange(index, 'manual_total_yards', productData.manual_total_yards);
      onItemChange(index, 'manual_total_rolls', productData.manual_total_rolls);
      onItemChange(index, 'price', productData.price);
      onItemChange(index, 'is_manual_totals', true);
      const displayName = productData.product_name + (productData.product_colorcode ? ` - ${productData.product_colorcode}` : '');
      setProductSearchTerms(prev => ({ ...prev, [index]: displayName }));
      setShowProductDropdown(prev => ({ ...prev, [index]: false }));
    }
  };

  const calculateTotal = (price, quantity, totalYards) => {
    const p = parseFloat(price) || 0;
    const q = parseFloat(quantity) || 0;
    const effectiveQty = parseFloat(totalYards) || q;
    return (p * effectiveQty).toFixed(0);
  };

  const getProductDisplayName = (item) => {
    if (item.product_name && item.product_colorcode) return `${item.product_name} - ${item.product_colorcode}`;
    else if (item.product_name) return item.product_name;
    else if (item.product_colorcode) return item.product_colorcode;
    return '';
  };

  const displayItems = items;

  // MOBILE VIEW
  if (isMobile) {
    return (
      <div className="items-mobile-container">
        <div className="items-header-mobile">
          <h4><FontAwesomeIcon icon={faBoxes} />{translations?.product || 'Products'}</h4>
          <button type="button" className="btn-add-item-mobile" onClick={() => onAdd()} disabled={disabled}>
            <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
          </button>
        </div>
        <div className="items-cards-container">
          {displayItems.map((item, index) => {
            const totalYardsValue = item.manual_total_yards || item.quantity || 0;
            const totalAmount = calculateTotal(item.price, item.quantity, totalYardsValue);
            
            return (
              <div key={item.id || index} className="item-card-mobile">
                <div className="item-card-header">
                  <span className="item-number"><FontAwesomeIcon icon={faBox} className="header-icon-only" />Item {index + 1}</span>
                  <button type="button" className="item-remove-btn-mobile" onClick={() => onRemove(index)}
                    disabled={disabled || displayItems.length === 1}><FontAwesomeIcon icon={faTrash} /></button>
                </div>
                <div className="item-card-content">
                  <div className={`mobile-field ${errors[`product_name_${index}`] ? 'has-error' : ''}`} ref={el => productSearchRefs.current[index] = el}>
                    <label><FontAwesomeIcon icon={faSearch} className="field-icon" />{translations?.productName || 'Product Name'}</label>
                    <div className="product-search-container">
                      <input type="text"
                        value={productSearchTerms[index] !== undefined ? productSearchTerms[index] : getProductDisplayName(item)}
                        onChange={(e) => handleProductSearch(index, e.target.value)}
                        onFocus={() => { setShowProductDropdown(prev => ({ ...prev, [index]: true })); if (!productSearchTerms[index]) setFilteredProducts(products); }}
                        placeholder={translations?.searchProduct || 'Search product...'} disabled={disabled}
                        dir={isRTL ? 'rtl' : 'ltr'} autoComplete="off" />
                      {loadingProducts && <div className="search-loading"><FontAwesomeIcon icon={faSpinner} spin /></div>}
                      {showProductDropdown[index] && (
                        <div className="product-dropdown">
                          {filteredProducts.length > 0 ? (
                            filteredProducts.map(product => (
                              <div key={product.id} className="product-option" onClick={() => selectProduct(index, product)}>
                                <div className="product-name">{product.product_name}{product.product_colorcode && ` - ${product.product_colorcode}`}</div>
                                <div className="product-stock">Stock: {product.total_yards || 0} yds</div>
                              </div>
                            ))
                          ) : (
                            <div className="product-dropdown empty"><FontAwesomeIcon icon={faExclamationTriangle} />{translations?.noProductsFound || 'No products found'}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Color Code column instead of Category */}
                  <div className="mobile-field">
                    <label>{translations?.colorCode || 'Color Code'}</label>
                    <input type="text" value={item.product_colorcode || ''}
                      onChange={(e) => onItemChange(index, 'product_colorcode', e.target.value)}
                      placeholder="Color Code" disabled={disabled} dir={isRTL ? 'rtl' : 'ltr'} />
                  </div>

                  {/* Unit Selector */}
                  <div className="mobile-field">
                    <label><FontAwesomeIcon icon={faRulerCombined} className="field-icon" />{translations?.selectUnit || 'Unit'}</label>
                    <select 
                      value={item.unit || 'yards'}
                      onChange={(e) => onItemChange(index, 'unit', e.target.value)}
                      disabled={disabled}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    >
                      {unitOptions.map(unit => (
                        <option key={unit.value} value={unit.value}>{unit.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Order Quantity */}
                  <div className={`mobile-field ${errors[`order_quantity_${index}`] ? 'has-error' : ''}`}>
                    <label><FontAwesomeIcon icon={faListOl} className="field-icon" />{translations?.orderQuantity || 'Order Quantity'}</label>
                    <input type="number" 
                      value={item.order_quantity || ''}
                      onChange={(e) => {
                        onItemChange(index, 'order_quantity', e.target.value);
                        if (!item.manual_total_yards) {
                          onItemChange(index, 'manual_total_yards', e.target.value);
                          onItemChange(index, 'quantity', e.target.value);
                        }
                      }}
                      step="1" min="0" placeholder="Enter order quantity" disabled={disabled} dir="ltr" />
                  </div>

                  {/* Order Rolls */}
                  <div className="mobile-field">
                    <label><FontAwesomeIcon icon={faBoxes} className="field-icon" />{translations?.orderRolls || 'Order Rolls'}</label>
                    <input type="number" 
                      value={item.order_rolls || ''}
                      onChange={(e) => {
                        onItemChange(index, 'order_rolls', e.target.value);
                        if (!item.manual_total_rolls && e.target.value) {
                          onItemChange(index, 'manual_total_rolls', e.target.value);
                        }
                      }}
                      step="1" min="0" placeholder="Optional" disabled={disabled} dir="ltr" />
                  </div>

                  {/* Price */}
                  <div className={`mobile-field ${errors[`price_${index}`] ? 'has-error' : ''}`}>
                    <label>{translations?.priceOptional || 'Price'} <span className="optional-badge">{translations?.optional || 'Optional'}</span></label>
                    <input type="number" value={item.price}
                      onChange={(e) => onItemChange(index, 'price', e.target.value)}
                      step="1" min="0" placeholder="0" disabled={disabled} dir="ltr" />
                  </div>

                  {/* Total Amount */}
                  <div className="mobile-field">
                    <label>{translations?.totalAmount || 'Total Amount'}</label>
                    <div className="total-display-mobile"><span>{currencySymbol} {totalAmount}</span></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // DESKTOP VIEW - Removed Category column, added Color Code column
  return (
    <div className="items-table-container">
      <div className="table-header-actions">
        <h4><FontAwesomeIcon icon={faBoxes} className="header-icon-only" />{translations?.product || 'Products'}</h4>
        <button type="button" className="btn-add-item-table" onClick={() => onAdd()} disabled={disabled}>
          <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
        </button>
      </div>
      <div className="table-wrapper" ref={tableRef}>
        <table className="items-table">
          <thead>
            <tr>
              <th className="sticky-col product-color-col">{translations?.productName || 'Product'}</th>
              <th className="sticky-col color-code-col">{translations?.colorCode || 'Color Code'}</th>
              <th className="sticky-col">{translations?.selectUnit || 'Unit'}</th>
              <th className="sticky-col">{translations?.orderQuantity || 'Order Qty'}</th>
              <th className="sticky-col">{translations?.orderRolls || 'Order Rolls'}</th>
              <th className="sticky-col price-col">{translations?.priceOptional || 'Price'} <span className="optional-badge">{translations?.optional || 'Optional'}</span></th>
              <th className="sticky-col total-col">{translations?.totalAmount || 'Total'}</th>
              <th className="sticky-col action-col"></th>
            </tr>
          </thead>
          <tbody>
            {displayItems.map((item, index) => {
              const totalYardsValue = item.manual_total_yards || item.quantity || 0;
              const totalAmount = calculateTotal(item.price, item.quantity, totalYardsValue);
              
              return (
                <tr key={item.id || index} className={errors[`product_name_${index}`] ? 'error-row' : ''}>
                  <td>
                    <div className="table-field product-search-field" ref={el => productSearchRefs.current[index] = el}>
                      <div className="product-search-container">
                        <input type="text"
                          value={productSearchTerms[index] !== undefined ? productSearchTerms[index] : getProductDisplayName(item)}
                          onChange={(e) => handleProductSearch(index, e.target.value)}
                          onFocus={() => { setShowProductDropdown(prev => ({ ...prev, [index]: true })); if (!productSearchTerms[index]) setFilteredProducts(products); }}
                          placeholder={translations?.searchProduct || 'Search product...'} disabled={disabled}
                          dir={isRTL ? 'rtl' : 'ltr'} className={errors[`product_name_${index}`] ? 'error' : ''} autoComplete="off" />
                        {loadingProducts && <div className="search-loading-small"><FontAwesomeIcon icon={faSpinner} spin /></div>}
                        {showProductDropdown[index] && (
                          <div className="product-dropdown-table">
                            {filteredProducts.length > 0 ? (
                              filteredProducts.map(product => (
                                <div key={product.id} className="product-option" onClick={() => selectProduct(index, product)}>
                                  <div className="product-name">{product.product_name}{product.product_colorcode && ` - ${product.product_colorcode}`}</div>
                                  <div className="product-stock">Stock: {product.total_yards || 0} yds</div>
                                </div>
                              ))
                            ) : (
                              <div className="product-dropdown-table empty"><FontAwesomeIcon icon={faExclamationTriangle} />{translations?.noProductsFound || 'No products found'}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="table-field">
                      <input type="text" value={item.product_colorcode || ''}
                        onChange={(e) => onItemChange(index, 'product_colorcode', e.target.value)}
                        placeholder="Color Code" disabled={disabled} dir={isRTL ? 'rtl' : 'ltr'} />
                    </div>
                  </td>
                  <td>
                    <div className="table-field">
                      <select 
                        value={item.unit || 'yards'}
                        onChange={(e) => onItemChange(index, 'unit', e.target.value)}
                        disabled={disabled}
                        dir={isRTL ? 'rtl' : 'ltr'}
                      >
                        {unitOptions.map(unit => (
                          <option key={unit.value} value={unit.value}>{unit.label}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className="table-field">
                      <input type="number" 
                        value={item.order_quantity || ''}
                        onChange={(e) => {
                          onItemChange(index, 'order_quantity', e.target.value);
                          if (!item.manual_total_yards) {
                            onItemChange(index, 'manual_total_yards', e.target.value);
                            onItemChange(index, 'quantity', e.target.value);
                          }
                        }}
                        step="1" min="0" placeholder="Qty" disabled={disabled} dir="ltr" />
                    </div>
                  </td>
                  <td>
                    <div className="table-field">
                      <input type="number" 
                        value={item.order_rolls || ''}
                        onChange={(e) => {
                          onItemChange(index, 'order_rolls', e.target.value);
                          if (!item.manual_total_rolls && e.target.value) {
                            onItemChange(index, 'manual_total_rolls', e.target.value);
                          }
                        }}
                        step="1" min="0" placeholder="Rolls" disabled={disabled} dir="ltr" />
                    </div>
                  </td>
                  <td>
                    <div className="table-field">
                      <input type="number" value={item.price}
                        onChange={(e) => onItemChange(index, 'price', e.target.value)}
                        step="1" min="0" placeholder="0" disabled={disabled} dir="ltr"
                        className={errors[`price_${index}`] ? 'error' : ''} />
                    </div>
                  </td>
                  <td><div className="table-field"><span className="total-amount">{currencySymbol} {totalAmount}</span></div></td>
                  <td>
                    <button type="button" className="table-remove-btn" onClick={() => onRemove(index)}
                      disabled={disabled || displayItems.length === 1} title={translations?.remove || 'Remove'}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// PaymentDetailsSection Component
const PaymentDetailsSection = ({ paymentMethod, formData, handleChange, handleFileUpload, saving, isRTL, translations, errors, currencySymbol }) => {
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [showChequeDetails, setShowChequeDetails] = useState(false);
  const [showCardDetails, setShowCardDetails] = useState(false);
  
  useEffect(() => {
    setShowBankDetails(paymentMethod === 'Transfer (Transaction OUT)');
    setShowChequeDetails(paymentMethod === 'Transfer (Transaction OUT)');
    setShowCardDetails(paymentMethod === 'Transfer (Transaction OUT)');
  }, [paymentMethod]);
  
  return (
    <div className="payment-details-section">
      {showBankDetails && (
        <div className="payment-details-card bank-details">
          <h4 className="payment-details-title"><FontAwesomeIcon icon={faUniversity} />Bank Transfer Details</h4>
          <div className="payment-details-grid">
            <div className={`form-group-modern ${errors.from_bank_name ? 'has-error' : ''}`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faUniversity} className="input-icon" />
                <input type="text" id="from_bank_name" name="from_bank_name" value={formData.from_bank_name || ''}
                  onChange={handleChange} placeholder="From Bank Name" disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} />
                <label htmlFor="from_bank_name" className="input-label">From Bank Name</label>
              </div>
            </div>
            <div className={`form-group-modern ${errors.from_account_number ? 'has-error' : ''}`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faCreditCard} className="input-icon" />
                <input type="text" id="from_account_number" name="from_account_number" value={formData.from_account_number || ''}
                  onChange={handleChange} placeholder="From Account Number" disabled={saving} dir="ltr" />
                <label htmlFor="from_account_number" className="input-label">From Account Number</label>
              </div>
            </div>
            <div className={`form-group-modern ${errors.to_bank_name ? 'has-error' : ''}`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faUniversity} className="input-icon" />
                <input type="text" id="to_bank_name" name="to_bank_name" value={formData.to_bank_name || ''}
                  onChange={handleChange} placeholder="To Bank Name" disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} />
                <label htmlFor="to_bank_name" className="input-label">To Bank Name</label>
              </div>
            </div>
            <div className={`form-group-modern ${errors.to_account_number ? 'has-error' : ''}`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faCreditCard} className="input-icon" />
                <input type="text" id="to_account_number" name="to_account_number" value={formData.to_account_number || ''}
                  onChange={handleChange} placeholder="To Account Number" disabled={saving} dir="ltr" />
                <label htmlFor="to_account_number" className="input-label">To Account Number</label>
              </div>
            </div>
            <div className="form-group-modern full-width">
              <div className="input-container">
                <FontAwesomeIcon icon={faComment} className="input-icon" />
                <input type="text" id="bank_reference" name="bank_reference" value={formData.bank_reference || ''}
                  onChange={handleChange} placeholder="Transaction Reference / UTR No" disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} />
                <label htmlFor="bank_reference" className="input-label">Reference / UTR Number</label>
              </div>
            </div>
            <div className="form-group-modern file-upload-group full-width">
              <label className="file-upload-label"><FontAwesomeIcon icon={faFileImage} />Payment Receipt / Screenshot</label>
              <div className="file-upload-container">
                <input type="file" id="payment_receipt" name="payment_receipt"
                  onChange={(e) => handleFileUpload(e, 'payment_receipt')} accept="image/*,.pdf" disabled={saving} className="file-input" />
                <div className="file-upload-preview">
                  {formData.payment_receipt ? (
                    <div className="file-preview"><FontAwesomeIcon icon={faFileImage} /><span>Receipt uploaded</span>
                      <button type="button" className="btn-remove-file" onClick={() => handleChange({ target: { name: 'payment_receipt', value: null } })}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  ) : (
                    <><FontAwesomeIcon icon={faUpload} /><span>Click to upload or drag & drop</span><small>PNG, JPG, PDF up to 5MB</small></>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showChequeDetails && (
        <div className="payment-details-card cheque-details">
          <h4 className="payment-details-title"><FontAwesomeIcon icon={faMoneyCheck} />Cheque Details</h4>
          <div className="payment-details-grid">
            <div className={`form-group-modern ${errors.cheque_number ? 'has-error' : ''}`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faBarcode} className="input-icon" />
                <input type="text" id="cheque_number" name="cheque_number" value={formData.cheque_number || ''}
                  onChange={handleChange} placeholder="Cheque Number" disabled={saving} dir="ltr" />
                <label htmlFor="cheque_number" className="input-label">Cheque Number</label>
              </div>
            </div>
            <div className={`form-group-modern ${errors.cheque_date ? 'has-error' : ''}`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                <input type="date" id="cheque_date" name="cheque_date" value={formData.cheque_date || ''}
                  onChange={handleChange} disabled={saving} dir="ltr" />
                <label htmlFor="cheque_date" className="input-label">Cheque Date</label>
              </div>
            </div>
            <div className={`form-group-modern ${errors.cheque_bank_name ? 'has-error' : ''}`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faUniversity} className="input-icon" />
                <input type="text" id="cheque_bank_name" name="cheque_bank_name" value={formData.cheque_bank_name || ''}
                  onChange={handleChange} placeholder="Bank Name" disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} />
                <label htmlFor="cheque_bank_name" className="input-label">Bank Name</label>
              </div>
            </div>
            <div className="form-group-modern">
              <div className="input-container">
                <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                <input type="text" id="cheque_branch" name="cheque_branch" value={formData.cheque_branch || ''}
                  onChange={handleChange} placeholder="Branch" disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} />
                <label htmlFor="cheque_branch" className="input-label">Branch</label>
              </div>
            </div>
            <div className="form-group-modern file-upload-group full-width">
              <label className="file-upload-label"><FontAwesomeIcon icon={faCamera} />Cheque Image / Scan</label>
              <div className="file-upload-container">
                <input type="file" id="cheque_image" name="cheque_image"
                  onChange={(e) => handleFileUpload(e, 'cheque_image')} accept="image/*" disabled={saving} className="file-input" />
                <div className="file-upload-preview">
                  {formData.cheque_image ? (
                    <div className="file-preview"><FontAwesomeIcon icon={faFileImage} /><span>Cheque image uploaded</span>
                      <button type="button" className="btn-remove-file" onClick={() => handleChange({ target: { name: 'cheque_image', value: null } })}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  ) : (
                    <><FontAwesomeIcon icon={faUpload} /><span>Click to upload cheque image</span><small>PNG, JPG up to 5MB</small></>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showCardDetails && (
        <div className="payment-details-card card-details">
          <h4 className="payment-details-title"><FontAwesomeIcon icon={faCreditCard} />Card Payment Details</h4>
          <div className="payment-details-grid">
            <div className={`form-group-modern ${errors.card_last_four ? 'has-error' : ''}`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faCreditCard} className="input-icon" />
                <input type="text" id="card_last_four" name="card_last_four" value={formData.card_last_four || ''}
                  onChange={handleChange} placeholder="Last 4 digits" maxLength="4" pattern="[0-9]{4}" disabled={saving} dir="ltr" />
                <label htmlFor="card_last_four" className="input-label">Card Last 4 Digits</label>
              </div>
            </div>
            <div className={`form-group-modern ${errors.card_holder_name ? 'has-error' : ''}`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faUser} className="input-icon" />
                <input type="text" id="card_holder_name" name="card_holder_name" value={formData.card_holder_name || ''}
                  onChange={handleChange} placeholder="Card Holder Name" disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} />
                <label htmlFor="card_holder_name" className="input-label">Card Holder Name</label>
              </div>
            </div>
            <div className="form-group-modern">
              <div className="input-container">
                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                <input type="text" id="card_expiry" name="card_expiry" value={formData.card_expiry || ''}
                  onChange={handleChange} placeholder="MM/YY" maxLength="5" disabled={saving} dir="ltr" />
                <label htmlFor="card_expiry" className="input-label">Expiry Date</label>
              </div>
            </div>
            <div className="form-group-modern">
              <div className="input-container">
                <FontAwesomeIcon icon={faQrcode} className="input-icon" />
                <input type="text" id="card_transaction_id" name="card_transaction_id" value={formData.card_transaction_id || ''}
                  onChange={handleChange} placeholder="Transaction ID" disabled={saving} dir="ltr" />
                <label htmlFor="card_transaction_id" className="input-label">Transaction ID</label>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="payment-details-card bill-receipt-card">
        <h4 className="payment-details-title"><FontAwesomeIcon icon={faFileInvoice} />Bill & Receipts</h4>
        <div className="payment-details-grid">
          <div className="form-group-modern file-upload-group full-width">
            <label className="file-upload-label"><FontAwesomeIcon icon={faFileImage} />Bill / Invoice Receipt</label>
            <div className="file-upload-container">
              <input type="file" id="bill_receipt" name="bill_receipt"
                onChange={(e) => handleFileUpload(e, 'bill_receipt')} accept="image/*,.pdf" disabled={saving} className="file-input" />
              <div className="file-upload-preview">
                {formData.bill_receipt ? (
                  <div className="file-preview"><FontAwesomeIcon icon={faFileImage} /><span>Bill receipt uploaded</span>
                    <button type="button" className="btn-remove-file" onClick={() => handleChange({ target: { name: 'bill_receipt', value: null } })}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ) : (
                  <><FontAwesomeIcon icon={faUpload} /><span>Click to upload bill/invoice</span><small>PNG, JPG, PDF up to 5MB</small></>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// MAIN COMPONENT
const FabPlaceOrder = ({ orderId = null, onSuccess = null }) => {
  const { user } = useAuth();
  const [shopId, setShopId] = useState(() => user?.shop_id || localStorage.getItem('current_shop_id') || '');
  const [currentCurrency, setCurrentCurrency] = useState(() => {
    const saved = localStorage.getItem('ourzest_currency');
    return saved ? JSON.parse(saved) : { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee', flag: '🇵🇰', shortcut: 'P' };
  });
  const [currencyRate, setCurrencyRate] = useState(1.0000);

  // Items state - Removed category, kept color code
  const [items, setItems] = useState([{
    id: Date.now(), 
    product_id: '', 
    product_name: '', 
    product_colorcode: '', 
    unit: 'yards',
    order_quantity: '',
    order_rolls: '',
    manual_total_yards: '', 
    manual_total_rolls: '', 
    price: '', 
    quantity: '', 
    is_manual_totals: true
  }]);

  // Form data state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0], expected_delivery: '', order_number: '',
    supplierid: '', supplier_name: '', company: '', status: 'Order Placed', container_no: '',
    route_details: '', paid: '0.00', balance: '0.00', payment_method: '',
    remarks: '',
    themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6', grand_total: '0.00',
    from_bank_name: '', from_account_number: '', to_bank_name: '', to_account_number: '',
    bank_reference: '', payment_receipt: null, cheque_number: '', cheque_date: '',
    cheque_bank_name: '', cheque_branch: '', cheque_image: null, card_last_four: '',
    card_holder_name: '', card_expiry: '', card_transaction_id: '', bill_receipt: null,
    order_receipt: null
  });

  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
  const [translations, setTranslations] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [toast, setToast] = useState({ show: false, type: '', message: '', direction: 'ltr', progress: 100, timeLeft: 3 });

  const toastTimerRef = useRef(null);
  const supplierSearchRef = useRef(null);
  const itemsRef = useRef(items);

  useEffect(() => { itemsRef.current = items; }, [items]);

  const handleCurrencyChange = (currency) => {
    setCurrentCurrency(currency);
    showToast('success', `Currency changed to ${currency.code} (${currency.symbol})`);
  };

  const handleRateChange = (rate) => { setCurrencyRate(rate); };

  const debouncedSupplierSearch = useCallback(debounce(async (term) => {
    if (!user?.username || !shopId) return;
    if (term.trim() === '') { setFilteredSuppliers(suppliers); setShowSupplierDropdown(true); return; }
    const filtered = suppliers.filter(supplier =>
      (supplier.supplier_name && supplier.supplier_name.toLowerCase().includes(term.toLowerCase())) ||
      (supplier.company && supplier.company.toLowerCase().includes(term.toLowerCase())) ||
      (supplier.phone && supplier.phone.includes(term))
    );
    if (filtered.length > 0) { setFilteredSuppliers(filtered); setShowSupplierDropdown(true); }
    else {
      try {
        const response = await fetch('https://ourzestapp.online/oz_backend/get_suppliers.php', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: user?.username, shop_id: shopId, searchTerm: term, page: 1, limit: 20 })
        });
        const data = await response.json();
        if (data.status === 'success') { setFilteredSuppliers(data.data || []); setShowSupplierDropdown(data.data && data.data.length > 0); }
      } catch (error) { console.error('Error searching suppliers:', error); }
    }
  }, 300), [suppliers, user?.username, shopId]);

  const showToast = useCallback((type, message) => {
    if (toastTimerRef.current) clearInterval(toastTimerRef.current);
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    const direction = isRTL ? 'rtl' : 'ltr';
    setToast({ show: true, type, message, direction, progress: 100, timeLeft: 3 });
    let timeLeft = 3;
    const timer = setInterval(() => {
      timeLeft -= 0.1;
      const progress = (timeLeft / 3) * 100;
      setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
      if (timeLeft <= 0) { clearInterval(timer); setToast(prev => ({ ...prev, show: false })); }
    }, 100);
    toastTimerRef.current = timer;
  }, [currentLanguage]);

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { showToast('error', 'File size should be less than 5MB'); return; }
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) { showToast('error', 'Only JPG, PNG, GIF and PDF files are allowed'); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, [fieldName]: { name: file.name, type: file.type, size: file.size, data: reader.result } }));
        showToast('success', `${fieldName} uploaded successfully`);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => { return () => { if (toastTimerRef.current) clearInterval(toastTimerRef.current); }; }, []);

  const loadTranslations = useCallback(async (lang) => {
    if (!lang) return;
    setIsTranslating(true);
    try {
      const trans = await placeOrderTranslator.getTranslations(lang);
      setTranslations(trans);
      const isRTL = lang === 'ur' || lang === 'ar';
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      document.body.dir = isRTL ? 'rtl' : 'ltr';
    } catch (error) { console.error('Error loading translations:', error); }
    finally { setIsTranslating(false); }
  }, []);

  useEffect(() => {
    loadTranslations(currentLanguage);
    const handleLanguageChange = () => {
      const newLang = localStorage.getItem('ourzest_language') || 'en';
      if (newLang !== currentLanguage) { setCurrentLanguage(newLang); loadTranslations(newLang); }
    };
    window.addEventListener('storage', handleLanguageChange);
    const intervalId = setInterval(() => {
      const storedLang = localStorage.getItem('ourzest_language') || 'en';
      if (storedLang !== currentLanguage) { setCurrentLanguage(storedLang); loadTranslations(storedLang); }
    }, 500);
    const translationListener = (lang) => { if (lang !== currentLanguage) { setCurrentLanguage(lang); loadTranslations(lang); } };
    placeOrderTranslator.addListener(translationListener);
    return () => {
      window.removeEventListener('storage', handleLanguageChange);
      clearInterval(intervalId);
      placeOrderTranslator.removeListener(translationListener);
    };
  }, [currentLanguage, loadTranslations]);

  useEffect(() => {
    const applyTheme = (color) => {
      try {
        if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16), g = parseInt(hex.substring(2, 4), 16), b = parseInt(hex.substring(4, 6), 16);
        const adjust = (amount) => {
          let newR = Math.max(0, Math.min(255, r + amount)), newG = Math.max(0, Math.min(255, g + amount)), newB = Math.max(0, Math.min(255, b + amount));
          return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
        };
        const palette = {
          'purchase-primary-color': color, 'purchase-primary-dark': adjust(-40), 'purchase-primary-light': adjust(40),
          'purchase-primary-ultralight': adjust(20), 'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
          'purchase-primary-shadow': `${color}20`
        };
        Object.entries(palette).forEach(([key, value]) => { document.documentElement.style.setProperty(`--${key}`, value); });
      } catch (err) { console.error("Error applying theme:", err); }
    };
    applyTheme(formData.themeColor);
    const handleThemeChange = () => {
      const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
      if (theme !== formData.themeColor) { setFormData(prev => ({ ...prev, themeColor: theme })); applyTheme(theme); }
    };
    handleThemeChange();
    window.addEventListener('storage', handleThemeChange);
    const interval = setInterval(handleThemeChange, 1000);
    return () => { window.removeEventListener('storage', handleThemeChange); clearInterval(interval); };
  }, [formData.themeColor]);

  const loadSuppliers = async () => {
    if (!user?.username || !shopId) return;
    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/get_suppliers.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user?.username, shop_id: shopId, page: 1, limit: 100 })
      });
      const data = await response.json();
      if (data.status === 'success') { setSuppliers(data.data || []); setFilteredSuppliers(data.data || []); }
      else { showToast('error', data.message || 'Failed to load suppliers'); }
    } catch (error) { console.error('Error loading suppliers:', error); showToast('error', 'Failed to load suppliers'); }
  };

  useEffect(() => { loadSuppliers(); }, [user?.username, shopId]);
  useEffect(() => { if (orderId) { setIsEditMode(true); loadOrderData(orderId); } }, [orderId]);
  useEffect(() => { calculateGrandTotal(); }, [items]);
  useEffect(() => {
    const total = parseFloat(formData.grand_total) || 0;
    const paid = parseFloat(formData.paid) || 0;
    const balance = total - paid;
    setFormData(prev => ({ ...prev, balance: balance.toFixed(2) }));
  }, [formData.grand_total, formData.paid]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (supplierSearchRef.current && !supplierSearchRef.current.contains(event.target)) setShowSupplierDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);

  const handleSupplierSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 1) setShowSupplierDropdown(true); else setShowSupplierDropdown(false);
    debouncedSupplierSearch(value);
  };

  const selectSupplier = (supplier) => {
    setFormData(prev => ({ ...prev, supplierid: supplier.supplierid, supplier_name: supplier.supplier_name || '', company: supplier.company || '' }));
    setSearchTerm(supplier.supplier_name || '');
    setShowSupplierDropdown(false);
    if (errors.supplier) setErrors(prev => ({ ...prev, supplier: '' }));
    showToast('success', `Supplier "${supplier.supplier_name}" selected`);
  };

  const addItem = () => {
    const newItem = {
      id: Date.now() + Math.random(), 
      product_id: '', 
      product_name: '', 
      product_colorcode: '',
      unit: 'yards',
      order_quantity: '',
      order_rolls: '',
      manual_total_yards: '', 
      manual_total_rolls: '', 
      price: '', 
      quantity: '',
      is_manual_totals: true
    };
    setItems(prev => [...prev, newItem]);
  };

  const removeItem = (index) => {
    setItems(prev => {
      if (prev.length > 1) {
        const updatedItems = [...prev];
        updatedItems.splice(index, 1);
        return updatedItems;
      }
      return prev;
    });
  };

  const handleItemChange = useCallback((index, field, value) => {
    setItems(prevItems => {
      const updatedItems = [...prevItems];
      if (!updatedItems[index]) updatedItems[index] = {};
      updatedItems[index] = { ...updatedItems[index], [field]: value };
      
      if (field === 'manual_total_yards') {
        updatedItems[index].quantity = value;
      }
      
      return updatedItems;
    });
    const errorKey = `${field}_${index}`;
    if (errors[errorKey]) setErrors(prev => { const newErrors = { ...prev }; delete newErrors[errorKey]; return newErrors; });
  }, [errors]);

  const calculateGrandTotal = () => {
    const grandTotal = items.reduce((sum, item) => {
      const qty = parseFloat(item.manual_total_yards) || parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      return sum + (price * qty);
    }, 0);
    setFormData(prev => ({ ...prev, grand_total: grandTotal.toFixed(2) }));
  };

  const loadOrderData = async (id) => {
    setLoading(true);
    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/fabrics_order.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_record', id: id, username: user?.username, shop_id: shopId })
      });
      const data = await response.json();
      if (data.status === 'success' && data.data) {
        const order = data.data;

        if (order.currency_code) {
          const savedCurrency = {
            code: order.currency_code,
            symbol: order.currency_symbol || 'Rs',
            name: order.currency_code,
            flag: '🇵🇰',
            shortcut: order.currency_code.charAt(0)
          };
          setCurrentCurrency(savedCurrency);
          setCurrencyRate(parseFloat(order.currency_rate) || 1.0000);
        }

        if (order.items && order.items.length > 0) {
          const loadedItems = order.items.map(item => ({
            id: Date.now() + Math.random(), 
            product_id: item.product_id || '', 
            product_name: item.product_name || '',
            product_colorcode: item.product_colorcode || '',
            unit: item.unit || 'yards',
            order_quantity: item.order_quantity || '',
            order_rolls: item.order_rolls || '',
            manual_total_yards: item.manual_total_yards || item.total_yards || item.quantity || '',
            manual_total_rolls: item.manual_total_rolls || '',
            price: item.price || '', 
            quantity: item.manual_total_yards || item.total_yards || item.quantity || '',
            is_manual_totals: true
          }));
          setItems(loadedItems);
        }
        setFormData({
          date: order.order_date || new Date().toISOString().split('T')[0], 
          expected_delivery: order.expected_delivery || '',
          order_number: order.order_number || '', 
          supplierid: order.supplierid || '', 
          supplier_name: order.supplier_name || '',
          company: order.company || '', 
          status: order.status || 'Order Placed', 
          container_no: order.container_no || '',
          route_details: order.route_details || '', 
          paid: order.paid_amount || '0', 
          balance: order.balance || '0',
          payment_method: order.payment_method || '',
          remarks: order.remarks || '', 
          themeColor: formData.themeColor,
          grand_total: order.total_amount || '0', 
          from_bank_name: order.from_bank_name || '',
          from_account_number: order.from_account_number || '', 
          to_bank_name: order.to_bank_name || '',
          to_account_number: order.to_account_number || '', 
          bank_reference: order.bank_reference || '',
          payment_receipt: order.payment_receipt || null, 
          cheque_number: order.cheque_number || '',
          cheque_date: order.cheque_date || '', 
          cheque_bank_name: order.cheque_bank_name || '',
          cheque_branch: order.cheque_branch || '', 
          cheque_image: order.cheque_image || null,
          card_last_four: order.card_last_four || '', 
          card_holder_name: order.card_holder_name || '',
          card_expiry: order.card_expiry || '', 
          card_transaction_id: order.card_transaction_id || '',
          bill_receipt: order.bill_receipt || null,
          order_receipt: order.order_receipt || null
        });
        if (order.supplier_name) setSearchTerm(order.supplier_name);
      } else { showToast('error', data.message || 'Failed to load order data'); }
    } catch (error) { showToast('error', 'Network error occurred'); }
    finally { setLoading(false); }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.supplierid) newErrors.supplier = translations?.supplierRequired || 'Supplier is required';
    if (!formData.status) newErrors.status = translations?.statusRequired || 'Status is required';

    const paid = parseFloat(formData.paid);
    if (isNaN(paid) || paid < 0) newErrors.paid = translations?.invalidPaid || 'Please enter a valid paid amount';

    if (formData.payment_method === 'Bank Transfer') {
      if (!formData.from_bank_name) newErrors.from_bank_name = 'From bank name is required';
      if (!formData.from_account_number) newErrors.from_account_number = 'From account number is required';
      if (!formData.to_bank_name) newErrors.to_bank_name = 'To bank name is required';
      if (!formData.to_account_number) newErrors.to_account_number = 'To account number is required';
    } else if (formData.payment_method === 'Cheque') {
      if (!formData.cheque_number) newErrors.cheque_number = 'Cheque number is required';
      if (!formData.cheque_date) newErrors.cheque_date = 'Cheque date is required';
      if (!formData.cheque_bank_name) newErrors.cheque_bank_name = 'Bank name is required';
    } else if (formData.payment_method === 'Credit Card') {
      if (!formData.card_last_four) newErrors.card_last_four = 'Last 4 digits are required';
      else if (!/^\d{4}$/.test(formData.card_last_four)) newErrors.card_last_four = 'Please enter valid 4 digits';
      if (!formData.card_holder_name) newErrors.card_holder_name = 'Card holder name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      const firstError = document.querySelector('.has-error, .error-message-modern');
      if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSaving(true);
    try {
      const currentUsername = user?.username || 'Unknown';
      const currentShopId = shopId || localStorage.getItem('current_shop_id');
      if (!currentUsername || currentUsername === 'Unknown') throw new Error("User not authenticated");
      if (!currentShopId) throw new Error("Shop ID is required. Please select a shop first.");

      const action = isEditMode ? 'edit' : 'add';
      const endpoint = 'https://ourzestapp.online/oz_backend/fabrics_order.php';

      const currentItems = itemsRef.current;

      const itemsData = currentItems.map(item => {
        const totalYards = parseFloat(item.manual_total_yards) || 0;
        const totalRolls = item.manual_total_rolls ? parseInt(item.manual_total_rolls) : null;
        const price = parseFloat(item.price) || 0;
        const totalAmount = price * totalYards;

        return {
          product_id: item.product_id ? parseInt(item.product_id) : null,
          product_name: item.product_name || '',
          product_colorcode: item.product_colorcode || null,
          unit: item.unit || 'yards',
          order_quantity: parseFloat(item.order_quantity) || totalYards,
          order_rolls: item.order_rolls ? parseInt(item.order_rolls) : totalRolls,
          manual_total_yards: totalYards,
          manual_total_rolls: totalRolls,
          is_manual_totals: true,
          price: price,
          quantity: totalYards,
          total: totalAmount.toFixed(0),
          total_amount: totalAmount.toFixed(0)
        };
      });

      const payload = {
        action: action, 
        username: currentUsername, 
        shop_id: parseInt(currentShopId),
        order_date: formData.date, 
        expected_delivery: formData.expected_delivery || null,
        order_number: formData.order_number || null, 
        supplierid: parseInt(formData.supplierid) || 0,
        supplier_name: formData.supplier_name || '', 
        company: formData.company || null,
        status: formData.status || 'Order Placed', 
        container_no: formData.container_no || null,
        route_details: formData.route_details || null, 
        paid_amount: parseFloat(formData.paid || 0),
        payment_method: formData.payment_method || '',
        remarks: formData.remarks || null,
        items: itemsData,
        from_bank_name: formData.from_bank_name || null, 
        from_account_number: formData.from_account_number || null,
        to_bank_name: formData.to_bank_name || null, 
        to_account_number: formData.to_account_number || null,
        bank_reference: formData.bank_reference || null,
        payment_receipt: formData.payment_receipt ? (typeof formData.payment_receipt === 'object' ? JSON.stringify(formData.payment_receipt) : formData.payment_receipt) : null,
        cheque_number: formData.cheque_number || null, 
        cheque_date: formData.cheque_date || null,
        cheque_bank_name: formData.cheque_bank_name || null, 
        cheque_branch: formData.cheque_branch || null,
        cheque_image: formData.cheque_image ? (typeof formData.cheque_image === 'object' ? JSON.stringify(formData.cheque_image) : formData.cheque_image) : null,
        card_last_four: formData.card_last_four || null, 
        card_holder_name: formData.card_holder_name || null,
        card_expiry: formData.card_expiry || null, 
        card_transaction_id: formData.card_transaction_id || null,
        bill_receipt: formData.bill_receipt ? (typeof formData.bill_receipt === 'object' ? JSON.stringify(formData.bill_receipt) : formData.bill_receipt) : null,
        currency_code: currentCurrency.code,
        currency_symbol: currentCurrency.symbol,
        currency_rate: currencyRate,
        order_receipt: formData.order_receipt ? (typeof formData.order_receipt === 'object' ? JSON.stringify(formData.order_receipt) : formData.order_receipt) : null
      };

      if (isEditMode && orderId) payload.id = orderId;

      const response = await fetch(endpoint, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      let data;
      try { data = JSON.parse(responseText); }
      catch (parseError) { throw new Error(`Server returned invalid JSON: ${responseText.substring(0, 200)}`); }

      if (data.status === 'success') {
        const successMessage = isEditMode ? translations?.orderUpdated || 'Order updated successfully!' : translations?.orderSaved || 'Order saved successfully!';
        showToast('success', successMessage);
        if (!isEditMode) handleClear();
        if (onSuccess) onSuccess(data.order_id || orderId);
      } else {
        const errorMessage = data.message || (isEditMode ? translations?.failedUpdate || 'Failed to update order' : translations?.failedSave || 'Failed to save order');
        showToast('error', errorMessage);
        console.error('Server error:', data);
      }
    } catch (error) {
      console.error('Submission error:', error);
      showToast('error', error.message || translations?.networkError || 'Network error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setItems([{
      id: Date.now(), 
      product_id: '', 
      product_name: '', 
      product_colorcode: '', 
      unit: 'yards',
      order_quantity: '',
      order_rolls: '',
      manual_total_yards: '', 
      manual_total_rolls: '', 
      price: '', 
      quantity: '', 
      is_manual_totals: true
    }]);
    setFormData({
      date: new Date().toISOString().split('T')[0], expected_delivery: '', order_number: '',
      supplierid: '', supplier_name: '', company: '', status: 'Order Placed', container_no: '',
      route_details: '', paid: '0.00', balance: '0.00', payment_method: '',
      remarks: '',
      themeColor: formData.themeColor, grand_total: '0.00', from_bank_name: '', from_account_number: '',
      to_bank_name: '', to_account_number: '', bank_reference: '', payment_receipt: null,
      cheque_number: '', cheque_date: '', cheque_bank_name: '', cheque_branch: '', cheque_image: null,
      card_last_four: '', card_holder_name: '', card_expiry: '', card_transaction_id: '', bill_receipt: null,
      order_receipt: null
    });
    setErrors({}); setSearchTerm(''); setIsEditMode(false);
  };

  const generateNextOrderNumber = () => {
    const date = new Date();
    const year = date.getFullYear().toString().substr(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `ORD-${year}${month}${day}-${random}`;
  };

  const handleGenerateOrderNumber = () => {
    if (!formData.order_number) setFormData(prev => ({ ...prev, order_number: generateNextOrderNumber() }));
  };

  const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
  const statusOptions = [
    { value: 'Order Placed', label: translations?.orderPlaced || 'Order Placed', icon: faCircleCheck, color: '#10b981' },
    { value: 'Processing', label: translations?.processing || 'Processing', icon: faCircleInfo, color: '#f59e0b' },
    { value: 'Shipped', label: translations?.shipped || 'Shipped', icon: faTruck, color: '#3b82f6' },
    { value: 'Delivered', label: translations?.delivered || 'Delivered', icon: faCheckCircle, color: '#10b981' },
    { value: 'Cancelled', label: translations?.cancelled || 'Cancelled', icon: faCircleExclamation, color: '#ef4444' }
  ];

  const paymentMethods = [
    { value: '', label: translations?.selectPaymentMethod || 'Select Payment Method', icon: null, disabled: true },
    { value: 'Transfer (Transaction OUT)', label: translations?.transfer || 'Transfer (Transaction OUT)', icon: faExchangeAlt }
  ];

  return (
    <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
      <ToastNotification toast={toast} setToast={setToast} translations={translations} />

      <div className="form-header-modern">
        <div className="header-content">
          <div className="header-icon"><FontAwesomeIcon icon={isEditMode ? faShoppingCart : faPlus} /></div>
          <div className="header-text">
            <h1>{isEditMode ? (translations?.editOrder || 'Edit Order') : (translations?.addOrder || 'Add Order')}</h1>
            <p>{translations?.orderDetails || 'Order Details'}</p>
          </div>
        </div>

        <CurrencySelector
          currentLanguage={currentLanguage}
          onCurrencyChange={handleCurrencyChange}
          onRateChange={handleRateChange}
        />

        {isTranslating && <div className="translation-indicator"><FontAwesomeIcon icon={faSpinner} spin /><span>Translating...</span></div>}
      </div>

      <form onSubmit={handleSubmit} className="modern-form">
        <div className="form-section">
          <h3 className="section-title"><FontAwesomeIcon icon={faReceipt} />{translations?.orderDetails || 'Order Information'}</h3>
          <div className="form-grid-modern">
            <div className={`form-group-modern ${errors.date ? 'has-error' : ''}`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                <input type="date" id="date" name="date" value={formData.date} onChange={handleChange} disabled={saving} dir="ltr" />
                <label htmlFor="date" className="input-label">{translations?.orderDate || 'Order Date'}<span className="required">*</span></label>
              </div>
              {errors.date && <div className="error-message-modern"><FontAwesomeIcon icon={faExclamationTriangle} />{errors.date}</div>}
            </div>
            <div className={`form-group-modern`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faClock} className="input-icon" />
                <input type="date" id="expected_delivery" name="expected_delivery" value={formData.expected_delivery} onChange={handleChange} disabled={saving} dir="ltr" />
                <label htmlFor="expected_delivery" className="input-label">{translations?.expectedDelivery || 'Expected Delivery'}</label>
              </div>
            </div>
            <div className={`form-group-modern ${errors.order_number ? 'has-error' : ''}`}>
              <div className="input-container">
                <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                <input type="text" id="order_number" name="order_number" value={formData.order_number} onChange={handleChange} required
                  placeholder={translations?.orderNumber || 'Enter order number'} disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} />
                <label htmlFor="order_number" className="input-label">{translations?.orderNumber || 'Order Number'}<span className="required">*</span></label>
                <button type="button" className="generate-btn" onClick={handleGenerateOrderNumber} disabled={saving || !!formData.order_number} title={translations?.generate || 'Generate'}>
                  <FontAwesomeIcon icon={faRedo} />
                </button>
              </div>
              {errors.order_number && <div className="error-message-modern"><FontAwesomeIcon icon={faExclamationTriangle} />{errors.order_number}</div>}
            </div>
            <div className={`form-group-modern ${errors.supplier ? 'has-error' : ''}`} ref={supplierSearchRef}>
              <div className="input-container">
                <FontAwesomeIcon icon={faSearch} className="input-icon search-icon" />
                <input type="text" id="supplier_search" value={searchTerm} onChange={handleSupplierSearchChange}
                  onFocus={() => { setShowSupplierDropdown(true); if (!searchTerm) setFilteredSuppliers(suppliers); }}
                  placeholder={translations?.searchSupplier || 'Search supplier...'} disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} autoComplete="off" />
                <label htmlFor="supplier_search" className="input-label">{translations?.supplier || 'Supplier'}<span className="required">*</span></label>
                <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />
              </div>
              {showSupplierDropdown && (
                <div className="supplier-dropdown">
                  {filteredSuppliers.length > 0 ? (
                    filteredSuppliers.map(supplier => (
                      <div key={supplier.supplierid} className="supplier-option" onClick={() => selectSupplier(supplier)}>
                        <div className="supplier-name">{supplier.supplier_name}</div>
                        <div className="supplier-info">
                          {supplier.phone && <span><FontAwesomeIcon icon={faPhone} /> {supplier.phone}</span>}
                          {supplier.company && <span>{supplier.company}</span>}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="supplier-dropdown empty"><FontAwesomeIcon icon={faExclamationTriangle} />{translations?.noSuppliersFound || 'No suppliers found'}</div>
                  )}
                </div>
              )}
              {errors.supplier && <div className="error-message-modern"><FontAwesomeIcon icon={faExclamationTriangle} />{errors.supplier}</div>}
            </div>
            <div className="form-group-modern">
              <div className="input-container">
                <FontAwesomeIcon icon={faUser} className="input-icon" />
                <input type="text" id="supplier_name" name="supplier_name" value={formData.supplier_name} readOnly
                  placeholder={translations?.supplierName || 'Supplier name will auto-fill'} disabled dir={isRTL ? 'rtl' : 'ltr'} className="readonly-input" />
                <label htmlFor="supplier_name" className="input-label">{translations?.supplierName || 'Supplier Name'}</label>
              </div>
            </div>
            <div className="form-group-modern">
              <div className="input-container">
                <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                <input type="text" id="company" name="company" value={formData.company} onChange={handleChange}
                  placeholder={translations?.companyOptional} disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} />
                <label htmlFor="company" className="input-label">{translations?.company || 'Company'}</label>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <PlaceOrderItemsTable
            items={items} onItemChange={handleItemChange} onRemove={removeItem} onAdd={addItem}
            errors={errors} disabled={saving} translations={translations} isRTL={isRTL}
            user={user} shopId={shopId} showToast={showToast} currencySymbol={currentCurrency.symbol}
          />
          {errors.items && <div className="error-message-modern items-error"><FontAwesomeIcon icon={faExclamationTriangle} />{errors.items}</div>}
        </div>

        <div className="form-section">
          <h3 className="section-title"><FontAwesomeIcon icon={faTruck} />Shipping & Payment</h3>
          <div className="form-grid-modern">
            <div className={`form-group-modern ${errors.status ? 'has-error' : ''}`}>
              <div className="input-container select-with-icon">
                {statusOptions.find(s => s.value === formData.status) && <FontAwesomeIcon icon={statusOptions.find(s => s.value === formData.status).icon} className="input-icon" />}
                <select id="status" name="status" value={formData.status} onChange={handleChange} disabled={saving} dir={isRTL ? 'rtl' : 'ltr'}>
                  {statusOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <label htmlFor="status" className="input-label">{translations?.status || 'Status'}<span className="required">*</span></label>
                <FontAwesomeIcon icon={faChevronDown} className="select-icon" />
              </div>
              {errors.status && <div className="error-message-modern"><FontAwesomeIcon icon={faExclamationTriangle} />{errors.status}</div>}
            </div>

            <div className="form-group-modern">
              <div className="input-container select-with-icon">
                {formData.payment_method && paymentMethods.find(m => m.value === formData.payment_method)?.icon &&
                  <FontAwesomeIcon icon={paymentMethods.find(m => m.value === formData.payment_method).icon} className="input-icon" />}
                <select id="payment_method" name="payment_method" value={formData.payment_method} onChange={handleChange} disabled={saving} dir={isRTL ? 'rtl' : 'ltr'}>
                  {paymentMethods.map(method => (
                    <option key={method.value} value={method.value} disabled={method.disabled}>
                      {method.label}
                    </option>
                  ))}
                </select>
                <label htmlFor="payment_method" className="input-label">{translations?.paymentMethod || 'Payment Method'}</label>
                <FontAwesomeIcon icon={faChevronDown} className="select-icon" />
              </div>
            </div>

            <div className="form-group-modern">
              <div className="input-container">
                <FontAwesomeIcon icon={faShip} className="input-icon" />
                <input type="text" id="container_no" name="container_no" value={formData.container_no} onChange={handleChange}
                  placeholder={translations?.containerOptional || 'Container No'} disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} />
                <label htmlFor="container_no" className="input-label">{translations?.containerNo || 'Container No'}</label>
              </div>
            </div>
            <div className="form-group-modern">
              <div className="input-container">
                <FontAwesomeIcon icon={faRoad} className="input-icon" />
                <input type="text" id="route_details" name="route_details" value={formData.route_details} onChange={handleChange}
                  placeholder={translations?.routeOptional || 'Route details'} disabled={saving} dir={isRTL ? 'rtl' : 'ltr'} />
                <label htmlFor="route_details" className="input-label">{translations?.routeDetails || 'Route Details'}</label>
              </div>
            </div>
          </div>
        </div>

        <PaymentDetailsSection
          paymentMethod={formData.payment_method}
          formData={formData}
          handleChange={handleChange}
          handleFileUpload={handleFileUpload}
          saving={saving}
          isRTL={isRTL}
          translations={translations}
          errors={errors}
          currencySymbol={currentCurrency.symbol}
        />

        <div className="form-actions-modern">
          <button type="button" className="btn-modern btn-secondary-modern" onClick={handleClear} disabled={saving}>
            <FontAwesomeIcon icon={faTimes} /><span>{translations?.clear || 'Clear'}</span>
          </button>
          <button type="submit" className="btn-modern btn-primary-modern" disabled={saving || isTranslating}>
            {saving ? (
              <><div className="btn-spinner"></div><span>{translations?.saving || 'Saving...'}</span></>
            ) : (
              <><span>{isEditMode ? (translations?.updateOrder || 'Update') : (translations?.saveOrder || 'Save')}</span><FontAwesomeIcon icon={faSave} /></>
            )}
          </button>
        </div>
      </form>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p>{translations?.saving || 'Loading...'}</p>
            <p className="loading-subtext">Please wait while we load order data</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FabPlaceOrder;